import { ReactNode, useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Flex, Separator, Spinner, Button, ScrollArea } from "@radix-ui/themes";
import { ChevronLeft, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog } from "@/components/shared/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/dataTabs/tabs";
import { useConversationViewApi } from "@/v2/entities/conversation-view/ConversationView.api";
import {
  ExploreCustomField,
  ExploreEnrichment,
  ExploreInteractionRow,
} from "@/v2/entities/explore/Explore.model";
import { Overview } from "./overview/Overview";
import { Evaluation } from "./evaluation/Evaluation";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { OverviewIcon } from "./assets/OverviewIcon";
import { EvaluationIcon } from "./assets/EvaluationIcon";
import { InteractionViewNavbar } from "./parts/InteractionViewNavbar";
import { InteractionViewContent } from "./parts/InteractionViewContent";
import { InteractionViewHeader } from "./parts/InteractionViewHeader";
import { InteractionViewTabContent } from "./parts/InteractionViewTabContent";
import { ActivityIcon } from "./assets/ActivityIcon";
import { useInteractionEventApi } from "@/v2/entities/interaction-event/InteractionEvent.api";
import { InteractionEvent } from "./interaction-event/InteractionEvent";
import { SendTicketToTraining } from "./training/SendTicketToTraining";
import { useTrackerApi } from "@/v2/entities/tracker/Tracker.api";
import { TrackerIcon } from "./assets/TracketIcon";
import { Tracker } from "./tracker/Tracker";
import { ConversationTranscript } from "./conversation-transcript/ConversationTranscript";
import { MarkAsValidated } from "./training/MarkAsValidated";
import ChainOfThoughts from "@/v2/features/simulator/parts/chainOfThoughts/ChainOfThoughts";
import { MessageComponent } from "@/components/quack-chat/core/parts/message/MessageComponent";
import { convertAiAgentStepsToChainOfThoughtSteps } from "@/v2/utils/aiAgentStepsConverter";
import { WalkthroughProvider } from "@/components/quack-chat/core/parts/WalkthroughContext";

export type ConversationViewTab =
  | "overview"
  | "evaluation"
  | "activity"
  | "tracker";

interface ConversationViewTabItem {
  label: string;
  value: ConversationViewTab;
  icon?: ReactNode;
  isLoading?: boolean;
}

interface ConversationViewContentProps {
  conversation: ExploreInteractionRow;
  enrichments: ExploreEnrichment[];
  customFields?: ExploreCustomField[];
  allowSendToTraining?: boolean;
  allowValidate?: boolean;
  onConversationValidated?: () => void;
  hideTranscript?: boolean;
}

export function ConversationViewContent({
  conversation,
  enrichments,
  customFields = [],
  allowSendToTraining = true,
  allowValidate = false,
  onConversationValidated,
  hideTranscript = false,
}: ConversationViewContentProps) {
  const queryClient = useQueryClient();
  const conversationViewApi = useConversationViewApi();
  const interactionEventApi = useInteractionEventApi();
  const trackerApi = useTrackerApi();

  const [selectedTab, setSelectedTab] =
    useState<ConversationViewTab>("overview");
  const [showChainOfThoughts, setShowChainOfThoughts] = useState(false);
  const [chainOfThoughtsSource, setChainOfThoughtsSource] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);

  const evaluationQuery = useQuery({
    queryKey: ["conversation-view", "evaluation", conversation.id],
    queryFn: () =>
      conversationViewApi.getConversationEvaluation(conversation.id),
  });
  const activityQuery = useQuery({
    queryKey: ["conversation-view", "activity", conversation.id],
    queryFn: () =>
      interactionEventApi.getInteractionEvents(conversation.id, false),
    enabled: true,
  });
  const insightsQuery = useQuery({
    queryKey: ["conversation-view", "insights", conversation.id],
    queryFn: () => conversationViewApi.getConversationInsights(conversation.id),
    select: (data) => {
      const latestInsight = data?.sort((a, b) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      })[0];

      if (latestInsight == null) {
        return;
      }

      const tldr = latestInsight.tldr;

      if (tldr == null || tldr === "") {
        return;
      }

      return tldr;
    },
  });
  const trackerQuery = useQuery({
    queryKey: ["conversation-view", "tracker", conversation.id],
    queryFn: () =>
      trackerApi.getTrackerItemBySource("conversations", conversation.id),
    enabled: true,
  });

  const tabList = useMemo<ConversationViewTabItem[]>(() => {
    const tabs: ConversationViewTabItem[] = [
      { label: "Overview", value: "overview", icon: <OverviewIcon /> },
    ];
    tabs.push({
      label: "Activity",
      value: "activity",
      icon: <ActivityIcon />,
      isLoading: activityQuery.isLoading,
    });
    tabs.push({
      label: "Evaluation",
      value: "evaluation",
      icon: <EvaluationIcon />,
      isLoading: evaluationQuery.isLoading,
    });
    tabs.push({
      label: "Tracker",
      value: "tracker",
      icon: <TrackerIcon />,
      isLoading: trackerQuery.isLoading,
    });
    
    return tabs;
  }, [
    evaluationQuery.isLoading,
    activityQuery.isLoading,
    trackerQuery.isLoading,
  ]);

  const handleUpdateTicketScore = useCallback(async () => {
    await Promise.all([
      evaluationQuery.refetch(),
      queryClient.invalidateQueries(["explore-v2"]),
    ]);
  }, [evaluationQuery.refetch, queryClient]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  }, []);

  return (
    <WalkthroughProvider>
      <Flex
        height={"100%"}
        pt={"1"}
        px={"1"}
        direction={"column"}
        overflow={"hidden"}
      >
        <InteractionViewNavbar />

        <ResizablePanelGroup
          direction="horizontal"
          className="h-full w-full overflow-hidden"
        >
          <ResizablePanel defaultSize={68} minSize={30}>
            <InteractionViewContent>
              <InteractionViewHeader
                interaction={conversation}
                rightAction={
                  <Button
                    variant="ghost"
                    size="1"
                    onClick={handleCopyLink}
                    aria-label="Copy link"
                  >
                    <LinkIcon size={16} />
                  </Button>
                }
              />

              <Flex pr={"24px"}>
                <Separator orientation="horizontal" size={"4"} />
              </Flex>

              <Flex
                height={"100%"}
                pt={"4"}
                direction={"column"}
                overflow={"hidden"}
              >
                <Flex px={"28px"} py={"3"} justify={"between"} align={"center"}>
                  <Tabs
                    value={selectedTab}
                    onValueChange={(value) =>
                      setSelectedTab(value as ConversationViewTab)
                    }
                  >
                    <TabsList size={"md"}>
                      {tabList.map((tab) => (
                        <TabsTrigger
                          key={tab.value}
                          value={tab.value}
                          size={"md"}
                          translate={tab.isLoading ? "no" : "yes"}
                        >
                          {tab.isLoading ? <Spinner size={"2"} /> : tab.icon}
                          {tab.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>

                  <Flex gap={"2"}>
                    {allowValidate && selectedTab === "evaluation" && (
                      <MarkAsValidated
                        interactionId={conversation.id}
                        onValidated={onConversationValidated}
                        source="conversations"
                      />
                    )}
                    {allowSendToTraining && (
                      <SendTicketToTraining
                        source="conversations"
                        interaction={conversation}
                      />
                    )}
                  </Flex>
                </Flex>

                {selectedTab === "overview" && (
                  <InteractionViewTabContent>
                    <Overview
                      source="conversations"
                      interaction={conversation}
                      enrichments={enrichments}
                      customFields={customFields}
                      summary={insightsQuery.data}
                      loadingSummary={insightsQuery.isLoading}
                    />
                  </InteractionViewTabContent>
                )}

                {selectedTab === "activity" && (
                  <InteractionViewTabContent
                    isLoading={activityQuery.isLoading}
                    isError={activityQuery.isError}
                    refetch={activityQuery.refetch}
                  >
                    <InteractionEvent data={activityQuery.data ?? []} />
                  </InteractionViewTabContent>
                )}

                {selectedTab === "evaluation" && (
                  <InteractionViewTabContent
                    isError={evaluationQuery.isError}
                    refetch={evaluationQuery.refetch}
                  >
                    <Evaluation
                      source="conversations"
                      interaction={conversation}
                      scores={evaluationQuery.data?.scores ?? []}
                      collaborators={evaluationQuery.data?.collaborators ?? []}
                      isLoading={evaluationQuery.isLoading}
                      onUpdateInteractionScore={handleUpdateTicketScore}
                    />
                  </InteractionViewTabContent>
                )}

                {selectedTab === "tracker" && (
                  <InteractionViewTabContent
                    isLoading={trackerQuery.isLoading}
                    isError={trackerQuery.isError}
                    refetch={trackerQuery.refetch}
                  >
                    <Tracker
                      data={trackerQuery.data ?? []}
                      refetch={trackerQuery.refetch}
                    />
                  </InteractionViewTabContent>
                )}
              </Flex>
            </InteractionViewContent>
          </ResizablePanel>

          {!hideTranscript && (
            <>
              <ResizableHandle withHandle />

              <ResizablePanel defaultSize={32} minSize={10}>
                <ConversationTranscript
                  conversation={conversation}
                  onChainOfThoughtsClick={(source) => {
                    const chainOfThoughtSteps =
                      source.chainOfThoughtSteps || [];

                    const aiAgentSteps = source.steps || [];
                    const convertedToolSteps =
                      convertAiAgentStepsToChainOfThoughtSteps(aiAgentSteps);

                    const allSteps = [
                      ...chainOfThoughtSteps,
                      ...convertedToolSteps,
                    ];

                    setChainOfThoughtsSource({
                      ...source,
                      chainOfThoughtSteps: allSteps,
                    });
                    setShowChainOfThoughts(true);
                  }}
                  onMessagesLoaded={setMessages}
                />
              </ResizablePanel>
            </>
          )}

          {showChainOfThoughts && chainOfThoughtsSource && (
            <>
              <ResizableHandle withHandle />

              <ResizablePanel defaultSize={30} minSize={25}>
                <Flex pl="2" py="2" justify="start">
                  <Button
                    variant="ghost"
                    size="1"
                    onClick={() => setShowChainOfThoughts(false)}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                </Flex>
                <ScrollArea scrollbars="vertical">
                  <div className="my-6">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex-1">
                        {(() => {
                          const message = messages.find(
                            (m) =>
                              m.chatMessageId ===
                              chainOfThoughtsSource.chatMessageId
                          );
                          if (message) {
                            return (
                              <MessageComponent
                                key={message.chatMessageId}
                                {...message}
                                customSVG={""}
                                userAvatarSVG={""}
                                svgRadius={undefined}
                                isLastMessage={false}
                                showAvatar={false}
                                showTimestamp={false}
                              />
                            );
                          }
                          return (
                            <h3 className="text-lg font-semibold">
                              Chain of thought
                            </h3>
                          );
                        })()}
                      </div>
                    </div>
                    <Flex direction="column" px="6">
                      <ChainOfThoughts
                        title="Chain of thought"
                        steps={chainOfThoughtsSource.chainOfThoughtSteps}
                        defaultOpen={true}
                        currentStepIndex={-1}
                        sources={
                          Array.isArray(chainOfThoughtsSource.contextExport)
                            ? chainOfThoughtsSource.contextExport
                            : []
                        }
                        isLoading={false}
                        hasTopic={true}
                        collapsible={false}
                      />
                    </Flex>
                  </div>
                  <Flex py="3" justify="start"></Flex>
                </ScrollArea>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </Flex>
    </WalkthroughProvider>
  );
}

interface ConversationViewProps extends ConversationViewContentProps {
  onClose: () => void;
}

export function ConversationView({
  onClose,
  ...contentProps
}: ConversationViewProps) {
  const handleOpenChanged = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Dialog open onOpenChange={handleOpenChanged}>
      <Dialog.Content size={"large"} className="rounded-lg h-[980px]">
        <Dialog.Close className="absolute top-[14px] right-[14px]" />

        <ConversationViewContent {...contentProps} />
      </Dialog.Content>
    </Dialog>
  );
}
